import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import "./App.css";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import Listings from "./pages/Listings";
import Localities from "./pages/Localities";
import LocalityGuide from "./pages/LocalityGuide";
import PropertyDetail from "./pages/PropertyDetail";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AuthCallback from "./pages/AuthCallback";
import AdminDashboard from "./pages/dashboard/AdminDashboard";
import VendorDashboard from "./pages/dashboard/VendorDashboard";
import SalesDashboard from "./pages/dashboard/SalesDashboard";
import BuyerDashboard from "./pages/dashboard/BuyerDashboard";

function DashboardRouter() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper" data-testid="dashboard-loading">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-black/50 animate-pulse-soft">Preparing your desk…</p>
      </div>
    );
  }
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  if (user.role === "super_admin") return <AdminDashboard />;
  if (user.role === "internal_sales") return <SalesDashboard />;
  if (user.role === "builder" || user.role === "agent") return <VendorDashboard />;
  return <BuyerDashboard />;
}

function Shell() {
  const location = useLocation();
  const isDashboard = location.pathname.startsWith("/dashboard");
  const isAuthPage = ["/login", "/register"].includes(location.pathname);
  const chrome = !isDashboard && !isAuthPage;

  useEffect(() => {
    if (isDashboard) return;
    const lenis = new Lenis({ smoothWheel: true });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, [isDashboard]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const lastTracked = useRef(null);
  useEffect(() => {
    const path = location.pathname + location.search;
    if (lastTracked.current === path) return;
    lastTracked.current = path;
    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: path,
        page_title: document.title,
      });
    }
  }, [location.pathname, location.search]);

  return (
    <>
      {chrome && <Navbar />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/listings" element={<Listings />} />
        <Route path="/localities" element={<Localities />} />
        <Route path="/localities/:slug" element={<LocalityGuide />} />
        <Route path="/property/:id" element={<PropertyDetail />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/dashboard" element={<DashboardRouter />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {chrome && <Footer />}
      <Toaster theme="dark" position="bottom-right" toastOptions={{ style: { background: "#111", border: "1px solid rgba(203,161,83,0.4)", color: "#F7F5F0", borderRadius: 0 } }} />
    </>
  );
}

function Root() {
  const location = useLocation();
  // Detect OAuth session_id synchronously during render (not in useEffect) to avoid races
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return <Shell />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Root />
      </BrowserRouter>
    </AuthProvider>
  );
}
